/**
 * Profitability API Client
 *
 * Interfaces with zeecore truth-based profitability endpoints
 * Base URL: http://localhost:4010/profitability
 */

import { authenticatedFetch } from '../utils/apiClient';

const API_BASE_URL = import.meta.env.VITE_ZEECORE_URL || 'http://localhost:4010';

// ============================================================================
// Types
// ============================================================================

export interface DailyProfitability {
  vehicle_id: string;
  vehicle_registration: string;
  date: string;
  revenue: {
    total: number;
    currency: string;
    order_count: number;
    orders: Array<{
      order_id: string;
      order_name: string;
      revenue: number;
      customer: string;
      timestamp: string;
    }>;
    palletline_adjustment?: number;
    collection_adjustment?: number;
  };
  costs: {
    fuel: {
      total: number;
      currency: string;
      source: 'jigsaw' | 'estimated';
      transactions: Array<{
        transaction_id?: string;
        timestamp: string;
        volume_litres: number;
        cost: number;
        fuel_type: string;
      }>;
      return_leg_estimate?: number;
    };
    driver: {
      total: number;
      currency: string;
      source: 'clockwatcher' | 'estimated';
      driver_name?: string;
      hours_worked: number;
      hourly_rate: number;
      actual_hours?: number;
    };
    mileage: {
      total_miles: number;
      loaded_miles: number;
      empty_miles: number;
      source: 'supatrak' | 'optifleet' | 'qargo_only' | 'estimated';
      trips: Array<{
        trip_id?: string;
        start_time: string;
        end_time?: string;
        distance_miles: number;
        is_loaded: boolean;
        start_address?: string;
        end_address?: string;
      }>;
      fuel_efficiency_mpg?: number;
    };
    overhead: {
      total: number;
      currency: string;
      source: 'configured' | 'estimated' | 'david_provided';
      breakdown: {
        depreciation: number;
        insurance: number;
        road_tax: number;
        mot_testing: number;
        maintenance?: number;
      };
      variable: {
        palletline_hub_fees?: number;
        tolls_congestion?: number;
        parking?: number;
      };
    };
    total: number;
  };
  net_profit: number;
  profit_margin_percent: number;
  qargo_estimate?: {
    profit: number;
    margin_percent: number;
  };
  confidence: {
    level: 'high' | 'medium' | 'low';
    score: number;
    data_sources: {
      revenue: 'observed' | 'estimated';
      fuel: 'observed' | 'estimated';
      driver: 'observed' | 'estimated';
      mileage: 'observed' | 'estimated';
      overhead: 'configured' | 'estimated';
    };
    missing: string[];
    recommendations: string[];
  };
  calculated_at: string;
}

export interface FleetProfitabilitySummary {
  period: {
    start: string;
    end: string;
  };
  fleet_summary: {
    total_revenue: number;
    total_costs: number;
    net_profit: number;
    avg_margin_percent: number;
    vehicle_count: number;
  };
  vehicles: DailyProfitability[];
  qargo_vs_actual: {
    qargo_total_profit: number;
    actual_total_profit: number;
    difference: number;
    difference_percent: number;
    message: string;
  };
  data_quality: {
    avg_confidence_score: number;
    vehicles_with_supatrak: number;
    vehicles_optifleet_only: number;
    vehicles_no_gps: number;
    missing_data_summary: string[];
  };
}

export interface ProfitabilityComparison {
  vehicle: string;
  date: string;
  qargo: {
    revenue: number;
    estimated_profit: number;
    margin_percent: number;
    order_count: number;
  };
  actual: {
    revenue: number;
    true_profit: number;
    margin_percent: number;
    total_costs: number;
    cost_breakdown: {
      fuel: number;
      driver: number;
      overhead: number;
      return_leg?: number;
      maintenance?: number;
    };
  };
  discrepancy: {
    profit_difference: number;
    margin_difference: number;
    missing_costs: number;
    explanation: string[];
  };
  timestamp: string;
}

export interface DataAvailabilityStatus {
  calculation_version: 'v1_basic' | 'v2_accurate';
  data_sources: {
    qargo_revenue: {
      available: boolean;
      status: string;
      description: string;
      coverage: string;
    };
    supatrak_mileage: {
      available: boolean;
      status: string;
      description: string;
      coverage: string;
      blocker?: string;
    };
    jigsaw_fuel: {
      available: boolean;
      status: string;
      description: string;
      coverage: string;
      issue?: string;
    };
    aquarius_driver_hours: {
      available: boolean;
      status: string;
      description: string;
      coverage: string;
    };
  };
  improvements_needed: string[];
  current_capabilities: string[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get daily profitability for a specific vehicle on a specific date
 */
export async function getDailyProfitability(
  vehicle: string,
  date: string,
  version: 'v1_basic' | 'v2_accurate' = 'v1_basic'
): Promise<DailyProfitability> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/profitability/daily/${encodeURIComponent(vehicle)}/${date}?version=${version}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to fetch daily profitability: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get fleet profitability summary for a date range
 */
export async function getFleetProfitabilitySummary(
  startDate: string,
  endDate: string,
  version: 'v1_basic' | 'v2_accurate' = 'v1_basic'
): Promise<FleetProfitabilitySummary> {
  const params = new URLSearchParams({
    startDate,
    endDate,
    version,
  });
  
  const response = await authenticatedFetch(
    `${API_BASE_URL}/profitability/summary?${params.toString()}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to fetch fleet profitability summary: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Compare Qargo profitability vs actual profitability
 */
export async function compareProfitability(
  vehicle: string,
  date: string
): Promise<ProfitabilityComparison> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/profitability/compare/${encodeURIComponent(vehicle)}/${date}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to compare profitability: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get data availability status for profitability calculations
 */
export async function getProfitabilityStatus(): Promise<DataAvailabilityStatus> {
  const response = await authenticatedFetch(`${API_BASE_URL}/profitability/status`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch profitability status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get profitability data for multiple vehicles in a date range
 */
export async function getBatchProfitability(
  vehicles: string[],
  startDate: string,
  endDate: string,
  version: 'v1_basic' | 'v2_accurate' = 'v1_basic'
): Promise<DailyProfitability[]> {
  // Fetch profitability for each vehicle-date combination
  const dates = getDatesInRange(startDate, endDate);
  
  const promises = vehicles.flatMap(vehicle =>
    dates.map(date =>
      getDailyProfitability(vehicle, date, version)
        .catch(err => {
          console.warn(`Failed to fetch profitability for ${vehicle} on ${date}:`, err);
          return null;
        })
    )
  );
  
  const results = await Promise.all(promises);
  return results.filter((r): r is DailyProfitability => r !== null);
}

// ============================================================================
// Helper Functions
// ============================================================================

function getDatesInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}
